import { avatarSrc } from "@/lib/defaults";
import { pausableInterval } from "@/lib/appVisibility";
import { adminConsoleLabel } from "@/lib/roles";
import { Flag } from "@/components/Flag";
import React from "react";
import { Capacitor } from "@capacitor/core";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp, LANGUAGES } from "@/context/AppContext";
import { sharedGet } from "@/lib/sharedGet";
import { FOLLOW_REQUESTS_EVENT, FOLLOW_REQUESTS_LINK } from "@/components/privacy/ProfilePrivacy";
import { LeftSidebar } from "@/components/LeftSidebar";
import { RightSidebar } from "@/components/RightSidebar";
import { NotificationBell } from "@/components/NotificationBell";
import { HeaderChatButton } from "@/components/HeaderChatButton";
import { NavigationDrawer, PLUS_MENU_ITEMS, PLUS_MENU_DEFAULT_ORDER } from "@/components/NavigationDrawer";
import { CompleteProfileModal } from "@/components/CompleteProfileModal";
import { InstallAppButton } from "@/components/InstallAppButton";
import { SiteFooter } from "@/components/SiteFooter";
import { YalolaLogo } from "@/components/YalolaLogo";
import { FeedUploadBanner } from "@/context/UploadQueueContext";
import { OfflineBanner } from "@/components/OfflineBanner";
import {
  Home as HomeIcon, Church, Video, Heart, LogOut,
  User as UserIcon, UserCheck, Film, Wrench, Settings, Moon, Sun, LifeBuoy,
  SlidersHorizontal, Receipt, Menu, Plus, MessageCircle, UserPlus, Library, Clapperboard,
  Users, CalendarDays, ShoppingBag, Radio, Bookmark,
} from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { isDarkMode, toggleDarkMode } from "@/lib/theme";
import { accountMenuKeys, zoneKeys } from "@/lib/layoutBlocks";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { StatusBarShim } from "@/components/StatusBarShim";
import { phoneNav } from "@/lib/platform";
import { api } from "@/lib/api";
import { topOrRefresh } from "@/lib/topRefresh";
import { toast } from "sonner";

/**
 * Center icon tabs (Facebook-style).
 * Same list used for desktop center + mobile bottom nav.
 */
const TABS = [
  { to: "/home",      icon: HomeIcon,   key: "home"      },
  { to: "/reels",     icon: Film,       key: "reels", feature: "reels" },
  { to: "/churches",  icon: Church,     key: "churches"  },
  { to: "/matching",  icon: Heart,      key: "matching", feature: "matching" },
];

// Mobile bottom tabs, keyed by the admin layout block keys (zone mobile_tabs — Admin › Mise en page).
// Home is pinned (always shown); the others can be toggled/reordered, optional tabs are OFF by default.
const MOBILE_TABS = {
  tab_home:        { to: "/home",        icon: HomeIcon,      key: "home" },
  tab_reels:       { to: "/reels",       icon: Film,          key: "reels",       feature: "reels" },
  tab_churches:    { to: "/churches",    icon: Church,        key: "churches" },
  tab_chat:        { to: "/messages",    icon: MessageCircle, key: "chat" },
  tab_profile:     { to: "/profile",     icon: UserIcon,      key: "profile" },
  tab_groups:      { to: "/groups",      icon: Users,         key: "groups",      feature: "groups" },
  tab_events:      { to: "/events",      icon: CalendarDays,  key: "events" },
  tab_marketplace: { to: "/marketplace", icon: ShoppingBag,   key: "marketplace", feature: "marketplace" },
  tab_live:        { to: "/live",        icon: Radio,         key: "live",        feature: "live" },
  tab_saved:       { to: "/saved",       icon: Bookmark,      key: "saved" },
};
const MOBILE_TABS_DEFAULT = ["tab_home", "tab_reels", "tab_churches", "tab_chat", "tab_profile"];
const MOBILE_TABS_MAX = 6;

// Reusable "Create" dropdown — Yalola create shortcuts (live, ad, event, church).
const CreateMenu = ({ children, align = "end", fEnabled, t, config }) => {
  const blocks = (config?.layout_blocks || [])
    .filter((b) => b.zone === "create_menu" && b.visible !== false && b.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  let keys = blocks.map((b) => (b.key || "").replace(/^create_/, ""));
  if (keys.length === 0) keys = PLUS_MENU_DEFAULT_ORDER; // fallback before layout blocks are seeded
  const items = keys
    .map((k) => ({ key: k, ...(PLUS_MENU_ITEMS[k] || {}) }))
    .filter((it) => it.to && (!it.feature || fEnabled(it.feature)));
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56" data-testid="mobile-create-menu">
        <DropdownMenuLabel>{t("nav.create", "Créer")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((c) => (
          <DropdownMenuItem key={c.key} asChild>
            <Link to={c.to} data-testid={`mobile-create-${c.key}`}><c.icon size={15} className="mr-2" />{t(c.tKey, c.fb)}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Native app (phones AND tablets): always the phone navigation — bottom tabs, centered « + »,
// ☰ drawer, no header tabs. Web keeps the md/lg breakpoints. Content columns are untouched.
const PHONE_NAV = phoneNav();

export const AppShell = ({ children, showSidebars = true, hideRightRail = false, immersive = false }) => {
  const { user, logout, lang, setLang, t, config, storiesUnseen } = useApp();
  const [dark, setDark] = React.useState(isDarkMode());
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selfOnline, setSelfOnline] = React.useState(true);
  const [myChurches, setMyChurches] = React.useState([]);
  const [navCounts, setNavCounts] = React.useState({ messages: 0, notifications: 0, requests: 0 });
  const nav = useNavigate();
  const loc = useLocation();
  const onReels = loc.pathname === "/reels";

  // Reflect the chat WebSocket connection on the user's own avatar (green dot).
  React.useEffect(() => {
    const on = (e) => setSelfOnline(!!e.detail?.connected);
    window.addEventListener("yalola:ws", on);
    return () => window.removeEventListener("yalola:ws", on);
  }, []);

  // Church pages the current preacher manages (for the "Gérer l'église" menu entry).
  React.useEffect(() => {
    if (user && user.role === "preacher" && user.is_verified) {
      api.get("/churches/mine").then(({ data }) => setMyChurches(data.churches || [])).catch(() => setMyChurches([]));
    } else {
      setMyChurches([]);
    }
  }, [user]);

  // Bottom-nav badges (mobile): unread messages (chat) + unread notifications.
  React.useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = (force = false) => {
      // Un seul appel (partagé avec HeaderChatButton / tiroir) : messages ET notifications non lus.
      sharedGet("/me/nav-badges", { ttl: 20000, force }).then((data) => { if (alive) setNavCounts((c) => ({ ...c, messages: data.messages_unread || 0, notifications: data.notifications_unread || 0, requests: data.follow_requests || 0 })); }).catch(() => {});
    };
    load();
    const stop = pausableInterval(() => load(false), 25000); // lot 699 — repos en arrière-plan
    const on = () => load(true);
    // Profil privé : demande acceptée / refusée / réglage changé → pastille « Demandes (n) » mise à jour sans attendre.
    const onRequests = (e) => { if (alive && typeof e.detail?.count === "number") setNavCounts((c) => ({ ...c, requests: e.detail.count })); };
    window.addEventListener("yalola:chat", on);
    window.addEventListener("yalola:read", on);
    window.addEventListener("yalola:notif", on);
    window.addEventListener(FOLLOW_REQUESTS_EVENT, onRequests);
    return () => {
      alive = false; stop();
      window.removeEventListener("yalola:chat", on);
      window.removeEventListener("yalola:read", on);
      window.removeEventListener("yalola:notif", on);
      window.removeEventListener(FOLLOW_REQUESTS_EVENT, onRequests);
    };
  }, [user]);

  // Edge swipe (from the left) opens the mobile navigation drawer.
  // iPhone natif : ce geste est le RETOUR du WebView (allowsBackForwardNavigationGestures) → jamais le menu.
  React.useEffect(() => {
    if (!user) return undefined;
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios") return undefined;
    let startX = 0, startY = 0, tracking = false;
    const onStart = (e) => {
      const tch = e.touches[0];
      if (window.innerWidth < 1024 && tch.clientX <= 24) { startX = tch.clientX; startY = tch.clientY; tracking = true; }
    };
    const onMove = (e) => {
      if (!tracking) return;
      const tch = e.touches[0];
      if (tch.clientX - startX > 55 && Math.abs(tch.clientY - startY) < 45) { setDrawerOpen(true); tracking = false; }
    };
    const onEnd = () => { tracking = false; };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
  }, [user]);

  // "Double-tap back to exit" (installed PWA, façon Facebook). Only active in
  // standalone mode on the home route, so it never breaks in-app navigation.
  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) return undefined; // native handles back via NativeBackButton
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const onHome = loc.pathname === "/" || loc.pathname === "/home";
    if (!standalone || !onHome) return undefined;
    window.history.pushState({ yalolaExitGuard: true }, "");
    let lastBack = 0;
    const onPop = () => {
      if (Date.now() - lastBack < 2000) return; // second press within 2s → allow exit
      lastBack = Date.now();
      toast(t("app.exit_hint", "Appuyez encore pour quitter"), { id: "yalola-exit-hint", position: "bottom-center", duration: 2000 });
      window.history.pushState({ yalolaExitGuard: true }, "");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [loc.pathname, t]);


  const features = config?.settings?.features || {};
  const fEnabled = (k) => features[k] !== false;
  const isAdmin = (Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.role]).some((r) => ["super_admin", "manager", "moderator", "associate", "support"].includes(r));
  const isHost = ["preacher", "artist", "influencer"].includes(user?.role);
  const maintenance = !!config?.settings?.maintenance_mode && !isAdmin;

  // Menu avatar (web) : mêmes entrées, même ordre et même visibilité que le tiroir mobile « Compte »
  // (Admin › Mise en page › Menu compte).
  const accountItem = (key) => {
    switch (key) {
      case "account_manage_church":
        if (myChurches.length === 1) return <DropdownMenuItem key={key} asChild><Link to={`/churches/${myChurches[0].id}`} data-testid="menu-manage-church"><Church size={14} className="mr-2" />{t("menu.manage_church", "Gérer l'église")}</Link></DropdownMenuItem>;
        return myChurches.length > 1 ? (
          <DropdownMenuSub key={key}>
            <DropdownMenuSubTrigger data-testid="menu-manage-church"><Church size={14} className="mr-2" />{t("menu.manage_church", "Gérer l'église")}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
              {myChurches.map((c) => (
                <DropdownMenuItem key={c.id} asChild><Link to={`/churches/${c.id}`} data-testid={`menu-manage-church-${c.id}`}>{c.name}</Link></DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null;
      case "account_resources":
        return features.resources === true ? <DropdownMenuItem key={key} asChild><Link to="/ressources" data-testid="menu-resources"><Library size={14} className="mr-2" />{t("nav.resources", "Ressources")}</Link></DropdownMenuItem> : null;
      case "account_settings":
        return <DropdownMenuItem key={key} asChild><Link to="/settings" data-testid="menu-settings"><SlidersHorizontal size={14} className="mr-2" />{t("menu.settings", "Paramètres")}</Link></DropdownMenuItem>;
      case "account_my_lives":
        return isHost ? <DropdownMenuItem key={key} asChild><Link to="/mes-directs" data-testid="menu-my-lives"><Clapperboard size={14} className="mr-2" />{t("menu.my_lives", "Mes directs")}</Link></DropdownMenuItem> : null;
      case "account_invite":
        return <DropdownMenuItem key={key} asChild><Link to="/invite" data-testid="menu-invite-friends"><UserPlus size={14} className="mr-2" />{t("invite.menu", "Inviter des amis")}</Link></DropdownMenuItem>;
      case "account_finances":
        return <DropdownMenuItem key={key} asChild><Link to={`/profile/${user.id}?tab=espace#paiements-finances`} data-testid="menu-history"><Receipt size={14} className="mr-2" />{t("menu.history", "Paiements & finances")}</Link></DropdownMenuItem>;
      case "account_support":
        return <DropdownMenuItem key={key} asChild><Link to="/support" data-testid="menu-support"><LifeBuoy size={14} className="mr-2" />{t("menu.support", "Support")}</Link></DropdownMenuItem>;
      case "account_admin":
        return isAdmin ? <DropdownMenuItem key={key} asChild><Link to="/admin" data-testid="menu-admin"><Settings size={14} className="mr-2" />{adminConsoleLabel(user, t)}</Link></DropdownMenuItem> : null;
      case "account_dark_mode":
        return (
          <DropdownMenuItem key={key} onSelect={(e) => { e.preventDefault(); toggleDarkMode(); setDark(isDarkMode()); }} data-testid="menu-dark-toggle">
            {dark ? <Sun size={14} className="mr-2" /> : <Moon size={14} className="mr-2" />}
            {dark ? t("menu.light", "Mode clair") : t("menu.dark", "Mode sombre")}
          </DropdownMenuItem>
        );
      default:
        return null;
    }
  };

  const isTabActive = (to) => {
    if (to === "/home") return loc.pathname === "/home" || loc.pathname === "/";
    return loc.pathname.startsWith(to);
  };
  // Lot 661 : onglet déjà actif (ou logo sur le fil) retapé → remonte en douceur ; déjà en haut → la page actualise.
  const onActiveTabClick = (realTo) => (e) => {
    const same = realTo === "/home" ? isTabActive("/home") : loc.pathname + loc.search === realTo || loc.pathname === realTo;
    if (!same) return;
    e.preventDefault();
    topOrRefresh();
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t("nav.see_you_soon", "À bientôt !"));
    nav("/");
  };

  if (maintenance) {
    return (
      <div className="min-h-screen grid place-items-center px-4" style={{ background: "var(--yalola-bg)" }}>
        <div className="max-w-md w-full text-center yalola-card p-10" data-testid="maintenance-screen">
          <div className="flex justify-center mb-4"><YalolaLogo size={48} /></div>
          <Wrench size={36} className="mx-auto mb-3" style={{ color: "var(--yalola-primary)" }} />
          <h1 className="text-2xl font-bold font-display">{t("maint.title", "Maintenance en cours")}</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--yalola-text-muted)" }}>
            {config?.settings?.maintenance_message || t("maint.body", "Yalola revient très vite. Merci de votre patience 🙏")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={immersive ? "min-h-screen" : `min-h-screen pb-[calc(3.5rem+var(--sa-bottom))] ${PHONE_NAV ? "" : "md:pb-0"}`} style={{ background: immersive ? "#000" : "var(--yalola-bg)" }} data-nav={PHONE_NAV ? "phone" : "responsive"}>
      {/* Top bar */}
      {!immersive && <StatusBarShim />}
      {!immersive && (
      <header className="sticky top-0 z-40 yalola-glass" style={{ paddingTop: "var(--sa-top)" }} data-testid="app-header" data-native-occluder="top">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-3 relative">
          {user && (
            <button
              className={`${PHONE_NAV ? "" : "lg:hidden"} h-10 w-10 grid place-items-center rounded-full transition-colors hover:bg-[color:var(--yalola-surface-alt)] shrink-0`}
              style={{ color: "var(--yalola-text)" }}
              onClick={() => setDrawerOpen(true)}
              aria-label={t("nav.menu", "Menu")}
              data-testid="mobile-menu-btn"
            >
              <Menu size={22} />
            </button>
          )}
          <Link to="/home" className="flex items-center gap-2 shrink-0" data-testid="brand-link" onClick={onActiveTabClick("/home")}>
            <YalolaLogo size={34} />
            <span className="hidden sm:block text-xl font-extrabold font-display tracking-tight">{config?.settings?.app_name || "Yalola"}</span>
          </Link>

          {/* Search — small on mobile, larger on desktop */}
          <SearchBar />

          {/* Center icon tabs — desktop only, centered relative to the feed */}
          {!PHONE_NAV && (
          <nav className="hidden md:flex items-center gap-2 absolute left-1/2 -translate-x-1/2" data-testid="header-tabs">
            {TABS.filter((tb) => !tb.feature || fEnabled(tb.feature)).map(({ to, icon: Icon, key }) => (
              <Link
                key={key}
                to={to}
                className={`tab-icon ${isTabActive(to) ? "is-active" : ""}`}
                data-testid={`tab-${key}`}
                title={t(`nav.${key}`, key)}
                onClick={onActiveTabClick(to)}
              >
                <Icon size={22} strokeWidth={isTabActive(to) ? 2.4 : 1.8} />
              </Link>
            ))}
          </nav>
          )}

          {/* Centered create "+" — phone (and every native screen); desktop web uses tabs + sidebar */}
          {user && (
            <div className={`${PHONE_NAV ? "" : "md:hidden"} absolute left-1/2 -translate-x-1/2 z-10`} data-testid="header-create-center">
              <CreateMenu align="center" fEnabled={fEnabled} t={t} config={config}>
                <button
                  className="yalola-create-fab h-11 w-11 rounded-full grid place-items-center text-white shadow-lg transition-transform active:scale-90"
                  style={{ background: "linear-gradient(135deg, var(--yalola-primary) 0%, var(--yalola-accent, #E0A63C) 100%)" }}
                  aria-label={t("nav.create", "Créer")}
                  data-testid="mobile-create-btn"
                >
                  <Plus size={26} strokeWidth={2.7} />
                </button>
              </CreateMenu>
            </div>
          )}

          {/* Right cluster */}
          <div className="flex items-center gap-1 ml-auto">
            {user && !PHONE_NAV && (
              <div className="hidden md:block lg:hidden">
                <CreateMenu align="end" fEnabled={fEnabled} t={t} config={config}>
                  <button className="tab-icon" aria-label={t("nav.create", "Créer")} data-testid="tablet-create-btn">
                    <Plus size={22} />
                  </button>
                </CreateMenu>
              </div>
            )}

            {user && !PHONE_NAV && <div className="hidden md:block"><HeaderChatButton /></div>}

            {/* Mobile only: Points en commun (Matching) — remonté depuis la barre du bas */}
            {user && fEnabled("matching") && (
              <Link to="/matching" className={`${PHONE_NAV ? "" : "md:hidden"} tab-icon`} aria-label={t("nav.matching", "Points en commun")} data-testid="header-matching-btn">
                <Heart size={20} fill={loc.pathname.startsWith("/matching") ? "currentColor" : "none"} />
              </Link>
            )}

            {/* Notifications — visible sur mobile (après le cœur) ET desktop (après le chat) */}
            {user && <NotificationBell />}

            {user ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button className={`${PHONE_NAV ? "hidden" : "hidden lg:flex"} items-center ml-1`} data-testid="user-menu-btn">
                    <span className="relative">
                      <Avatar className="h-8 w-8 md:h-9 md:w-9 border-2" style={{ borderColor: "var(--yalola-primary)" }}>
                        <AvatarImage src={avatarSrc(user)} />
                        <AvatarFallback style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)", fontWeight: 700 }}>
                          {user.name?.[0]?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      {selfOnline && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white" style={{ background: "#22c55e" }} data-testid="self-online-dot" />}
                      {navCounts.requests > 0 && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white" style={{ background: "#DC2626" }} data-testid="header-avatar-requests-dot" />}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="font-semibold">{user.name}</div>
                    <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to={`/profile/${user.id}`} data-testid="menu-my-profile"><UserIcon size={14} className="mr-2" />{t("menu.my_profile", "Mon profil")}</Link></DropdownMenuItem>
                  {navCounts.requests > 0 && (
                    <DropdownMenuItem asChild>
                      <Link to={FOLLOW_REQUESTS_LINK} className="font-semibold" data-testid="menu-follow-requests" data-count={navCounts.requests}>
                        <UserCheck size={14} className="mr-2" style={{ color: "#DC2626" }} />{t("profile.follow_requests_btn", "Demandes ({n})").replace("{n}", String(navCounts.requests))}
                        <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold text-white" style={{ background: "#DC2626" }}>{navCounts.requests > 9 ? "9+" : navCounts.requests}</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {accountMenuKeys(config).map(accountItem)}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[11px] uppercase" style={{ color: "var(--yalola-text-muted)" }}>{t("menu.language", "Langue")}</DropdownMenuLabel>
                  <div className="grid grid-cols-4 gap-1 px-2 pb-2">
                    {LANGUAGES.map(l => (
                      <button
                        key={l.code}
                        onClick={() => setLang(l.code)}
                        className={`text-xs py-1.5 rounded-lg ${lang === l.code ? "font-bold" : ""}`}
                        style={{ background: lang === l.code ? "var(--yalola-primary-soft)" : "transparent", color: lang === l.code ? "var(--yalola-primary)" : "var(--yalola-text)" }}
                        data-testid={`lang-${l.code}`}
                      ><span className="flex flex-col items-center gap-0.5"><Flag code={l.code} size={16} /><span className="uppercase">{l.code}</span></span></button>
                    ))}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut size={14} className="mr-2" /> {t("menu.logout", "Déconnexion")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/?tab=login" data-testid="header-signin">
                <Button className="pill pill-primary h-9">{t("nav.sign_in", "Connexion")}</Button>
              </Link>
            )}
          </div>
        </div>
      </header>
      )}
      {!immersive && <OfflineBanner />}

      <NavigationDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />

      <CompleteProfileModal />

      <InstallAppButton variant="banner" />
      <InstallAppButton variant="popup" />

      <main className={immersive ? "w-full" : "max-w-[1400px] mx-auto px-3 sm:px-6 py-4 md:py-5"}>
        {immersive ? children : (!user && showSidebars) ? (
        /* Visiteur (pages publiques) : aucune barre latérale — contenu et pied de page centrés. */
        <div className={`${hideRightRail ? "max-w-5xl" : "max-w-[680px]"} mx-auto w-full`} data-testid="guest-main">
          {children}
        </div>
        ) : (
        <div className={`grid grid-cols-1 ${showSidebars ? "lg:grid-cols-12" : ""} gap-4 md:gap-6`}>
          {showSidebars && (
            <aside className="hidden lg:block lg:col-span-3">
              <div className="sticky top-20 lg:h-[calc(100vh-6rem)] lg:overflow-y-auto yalola-scroll-hover pr-1">
                <LeftSidebar />
              </div>
            </aside>
          )}
          <section className={showSidebars ? (hideRightRail ? "lg:col-span-9" : "lg:col-span-6") : "col-span-1"}>
            {/* Global upload progress: visible on EVERY page (profile, church…), not just the feed */}
            <div className="empty:hidden mb-4">
              <FeedUploadBanner />
            </div>
            {children}
          </section>
          {showSidebars && !hideRightRail && (
            <aside className="hidden lg:block lg:col-span-3">
              <div className="sticky top-20 lg:h-[calc(100vh-6rem)] lg:overflow-y-auto yalola-scroll-hover space-y-4 pl-1">
                <RightSidebar />
              </div>
            </aside>
          )}
        </div>
        )}
      </main>

      {/* Visiteur web (robots inclus) : pied de page éditorial + légal sur chaque page publique */}
      {!immersive && !user && <SiteFooter />}

      {/* Mobile bottom nav — always visible, native-app feel */}
      <nav
        className={`${PHONE_NAV ? "" : "md:hidden"} fixed bottom-0 left-0 right-0 z-40 border-t ${onReels ? "yalola-reels-nav" : "bg-white"}`}
        style={onReels
          ? { borderColor: "rgba(255,255,255,0.12)", paddingBottom: "var(--sa-bottom)", background: "rgba(0,0,0,0.55)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }
          : { borderColor: "var(--yalola-border)", paddingBottom: "var(--sa-bottom)" }}
        data-testid="mobile-bottom-nav"
        data-native-occluder="bottom"
      >
        <div className="flex items-center justify-around h-14">
          {zoneKeys(config, "mobile_tabs", MOBILE_TABS_DEFAULT)
            .map((k) => MOBILE_TABS[k])
            .filter((tb) => tb && (!tb.feature || fEnabled(tb.feature)))
            .slice(0, MOBILE_TABS_MAX)
            .map(({ to, icon: Icon, key }) => {
            const realTo = key === "profile" && user ? `/profile/${user.id}` : to;
            const active = isTabActive(to);
            const badge = key === "chat" ? navCounts.messages : key === "profile" ? navCounts.requests : 0;
            return (
              <Link
                key={key}
                to={realTo}
                className={`flex-1 flex flex-col items-center justify-center py-2 transition-transform active:scale-90`}
                style={{ color: active ? "var(--yalola-primary)" : (onReels ? "rgba(255,255,255,0.9)" : "var(--yalola-text-muted)") }}
                data-testid={`mobile-tab-${key}`}
                onClick={onActiveTabClick(realTo)}
              >
                <span className="relative">
                  {key === "profile" ? (
                    <span className="relative inline-flex" data-testid="mobile-tab-profile-avatar">
                      <Avatar className="h-7 w-7 border-2" style={{ borderColor: active ? "var(--yalola-primary)" : (onReels ? "rgba(255,255,255,0.85)" : "var(--yalola-border)") }}>
                        <AvatarImage src={avatarSrc(user)} />
                        <AvatarFallback style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)", fontWeight: 700, fontSize: "11px" }}>{user?.name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
                      </Avatar>
                      {selfOnline && <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full border-2" style={{ background: "#22c55e", borderColor: onReels ? "#000" : "var(--yalola-surface)" }} data-testid="mobile-tab-profile-online" />}
                    </span>
                  ) : (
                    <Icon size={22} strokeWidth={active ? 2.4 : 1.9} />
                  )}
                  {key === "home" && storiesUnseen > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-1 rounded-full grid place-items-center text-[9px] font-bold text-white"
                      style={{ background: "var(--yalola-primary)", boxShadow: "0 0 0 2px var(--yalola-surface)" }}
                      data-testid="mobile-feed-unseen-badge"
                    >
                      {storiesUnseen > 9 ? "9+" : storiesUnseen}
                    </span>
                  )}
                  {badge > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-1 rounded-full grid place-items-center text-[9px] font-bold text-white"
                      style={{ background: key === "profile" ? "#DC2626" : "var(--yalola-primary)", boxShadow: "0 0 0 2px var(--yalola-surface)" }}
                      data-testid={`mobile-tab-badge-${key}`}
                    >
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </span>
                {active && <span className="mt-1 h-1 w-6 rounded-full" style={{ background: "var(--yalola-primary)" }} />}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
